import React, { useState, useRef, useEffect } from 'react';
import { useGeneralContext } from '../../context';
import { useLocation } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'react-feather';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import simpleLogo from '../../resources/logo-simple.png';
import JumbleModal from '../../components/jumbleModal/JumbleModal';
import { SupportedCurrencyCode, isSupportedCurrencyCode, premiumFeatures } from './data';
import { createCheckoutSession } from '../../api/stripe-api';
import { SubscriptionPlan, isSubscriptionPlan } from '../settings/types';
import config from '../../config';

const ChoosePlan = () => {
  const context = useGeneralContext();
  const { nickname, plansModalOpen: showPlans, setPlansModalOpen } = context;
  const closeModal = () => setPlansModalOpen(false);
  const isLandingPage = useLocation().pathname === '/';
  return (
    <JumbleModal
      showModal={showPlans}
      closeModal={closeModal}
      modalSections={[
        () => <PlanSelectionSection showPlans={showPlans} closeModal={closeModal} isLandingPage={isLandingPage} />,
        () => <EncouragementMessage nickname={nickname} isLandingPage={isLandingPage} />
      ]}
    />
  );
};

const PlanSelectionSection = (props: { showPlans: boolean; closeModal: () => void; isLandingPage: boolean }) => {
  const context = useGeneralContext();
  const { currency } = context;
  const { closeModal, isLandingPage } = props;

  const location = useLocation();

  const [loading, setLoading] = useState<boolean>(false);

  const modalRef = useRef<HTMLDivElement>(null);

  const queryParams = new URLSearchParams(location.search);
  const plan = queryParams.get('plan');
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | 'lifetime' | null>(
    (plan && isSubscriptionPlan(plan)) || plan === 'lifetime' ? plan : 'premium_yearly'
  );

  useEffect(() => {
    function handleClickOutside(event: any) {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        closeModal();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    // Clean up the listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [closeModal]);

  const plans: { name: string; id: SubscriptionPlan; price: string; savings?: string; mostPopular?: boolean; perDayPrice?: string }[] = [
    {
      name: 'Monthly',
      id: 'premium_monthly',
      price: `${currency.symbol}${currency.code === 'aud' ? '10' : '5'} / month`
    },
    {
      name: 'Annually',
      id: 'premium_yearly',
      price: `${currency.symbol}${currency.code === 'aud' ? '100' : '50'} / year`,
      savings: currency.code === 'aud' ? 'Save $20' : 'Save $10',
      mostPopular: true,
      perDayPrice: currency.code === 'aud' ? 'Less than $0.28/day' : 'Less than $0.14/day'
    }
  ];

  const lifetimeDeal = {
    name: 'Lifetime Access',
    id: 'lifetime',
    price: `Single payment of ${currency.symbol}${currency.code === 'aud' ? '350' : '175'}`
  };

  const selectedPlanPrice =
    selectedPlan === 'lifetime' ? lifetimeDeal.price : plans.find((plan) => plan.id === selectedPlan)?.price;

  const title = isLandingPage ? 'Premium Plans' : 'Choose your plan';
  const closeButtonText = isLandingPage ? 'Close' : 'Cancel';
  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-gap-6">
      <div className="tw-flex tw-flex-row tw-justify-start tw-items-center tw-mb-4 tw-gap-4 tw-w-full">
        <img alt="logo" className="tw-w-[30px] tw-h-auto" src={simpleLogo}></img>
        <div className="tw-font-bold">{title}</div>
      </div>
      <div
        className={`tw-w-full tw-flex tw-flex-col tw-gap-2 tw-py-3 tw-px-5 tw-justify-center tw-self-stretch tw-rounded-lg tw-border-2 tw-bg-success-50 tw-border-success-200`}
      >
        <div className="tw-self-center tw-font-bold tw-text-lg tw-text-success-800">{`${config.freeTrialPeriodInDays}-Day Free Trial`}</div>
        <div className="tw-self-center tw-text-base tw-text-success-700 tw-font-medium">Included with all plans</div>
      </div>
      <div className="tw-text-neutral-400 tw-font-medium tw-text-sm">Choose your plan:</div>
      {plans.map((plan, index) => {
        const isSelected = selectedPlan === plan.id;
        return (
          <div key={`plan-${index}`} className="tw-relative tw-w-full">
            {plan.mostPopular && (
              <div className="tw-absolute tw--top-3 tw-left-1/2 tw-transform tw--translate-x-1/2 tw-z-10">
                <span className="tw-bg-primary-600 tw-text-white tw-text-xs tw-font-semibold tw-px-3 tw-py-1 tw-rounded-full tw-shadow-sm">
                  Most Popular
                </span>
              </div>
            )}
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id={`plan-comparison-${plan.id}`}>
                  {plan.id === 'premium_yearly' ? (
                    <div>
                      <strong>Best Value!</strong><br />
                      • Save {currency.code === 'aud' ? '$20' : '$10'} vs monthly<br />
                      • Only {plan.perDayPrice}<br />
                      • Most popular choice
                    </div>
                  ) : (
                    <div>
                      <strong>Monthly Flexibility</strong><br />
                      • Month-to-month billing<br />
                      • Cancel anytime<br />
                      • Same features as annual
                    </div>
                  )}
                </Tooltip>
              }
            >
              <button
                className={`tw-w-full tw-flex tw-flex-col tw-gap-3 tw-py-5 tw-px-6 tw-justify-center tw-self-stretch tw-rounded-lg tw-border-2 tw-transition-all tw-duration-200 tw-min-h-[80px] ${
                  isSelected
                    ? 'tw-bg-primary-50 tw-border-primary-500 tw-shadow-lg tw-scale-[1.02]'
                    : 'tw-bg-white tw-border-neutral-200 tw-hover:border-primary-300 tw-hover:shadow-md'
                }`}
                onClick={() => {
                  setSelectedPlan(plan.id);
                }}
                disabled={loading}
              >
                <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <div className={`tw-w-6 tw-h-6 tw-rounded-full tw-border-2 tw-flex tw-items-center tw-justify-center ${
                      isSelected ? 'tw-border-primary-500 tw-bg-primary-500' : 'tw-border-neutral-300'
                    }`}>
                      {isSelected && (
                        <div className="tw-w-3 tw-h-3 tw-rounded-full tw-bg-white"></div>
                      )}
                    </div>
                    <div className="tw-text-left">
                      <div className="tw-font-bold tw-text-lg tw-text-neutral-800">{plan.name}</div>
                      <div className="tw-text-base tw-text-neutral-600">{plan.price}</div>
                      {plan.perDayPrice && (
                        <div className="tw-text-sm tw-text-neutral-500">{plan.perDayPrice}</div>
                      )}
                    </div>
                  </div>
                  {plan.savings && (
                    <div className="tw-bg-success-50 tw-text-success-700 tw-text-sm tw-font-medium tw-px-3 tw-py-2 tw-rounded-md">
                      {plan.savings}
                    </div>
                  )}
                </div>
              </button>
            </OverlayTrigger>
          </div>
        );
      })}
      <div className="tw-text-neutral-500">OR</div>
      <div className="tw-relative tw-w-full">
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id="plan-comparison-lifetime">
              <div>
                <strong>One-Time Payment</strong><br />
                • Never pay again<br />
                • 30-day money-back guarantee<br />
                • All future features included
              </div>
            </Tooltip>
          }
        >
          <button
            className={`tw-w-full tw-flex tw-flex-col tw-gap-3 tw-py-5 tw-px-6 tw-justify-center tw-self-stretch tw-rounded-lg tw-border-2 tw-transition-all tw-duration-200 tw-min-h-[80px] ${
              selectedPlan === 'lifetime'
                ? 'tw-bg-primary-50 tw-border-primary-500 tw-shadow-lg tw-scale-[1.02]'
                : 'tw-bg-white tw-border-neutral-200 tw-hover:border-primary-300 tw-hover:shadow-md'
            }`}
            onClick={() => {
              setSelectedPlan('lifetime');
            }}
            disabled={loading}
          >
            <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
              <div className="tw-flex tw-items-center tw-gap-3">
                <div className={`tw-w-6 tw-h-6 tw-rounded-full tw-border-2 tw-flex tw-items-center tw-justify-center ${
                  selectedPlan === 'lifetime' ? 'tw-border-primary-500 tw-bg-primary-500' : 'tw-border-neutral-300'
                }`}>
                  {selectedPlan === 'lifetime' && (
                    <div className="tw-w-3 tw-h-3 tw-rounded-full tw-bg-white"></div>
                  )}
                </div>
                <div className="tw-text-left">
                  <div className="tw-font-bold tw-text-lg tw-text-neutral-800">{lifetimeDeal.name}</div>
                  <div className="tw-text-base tw-text-neutral-600">{lifetimeDeal.price}</div>
                </div>
              </div>
            </div>
          </button>
        </OverlayTrigger>
      </div>
      <ValuePropositionSection />
      <PremiumFeatures />
      {isLandingPage ? (
        <div className="tw-mt-8 tw-flex tw-justify-center tw-w-full tw-border-t tw-border-neutral-200 tw-pt-6">
          <button
            disabled={loading}
            onClick={() => {
              closeModal();
            }}
            className="tw-flex tw-py-2 tw-px-6 tw-justify-center tw-items-center tw-gap-2 tw-rounded-md tw-text-sm tw-font-medium tw-text-neutral-500 tw-bg-transparent tw-min-h-[40px] tw-transition-all tw-duration-200 hover:tw-text-neutral-700 hover:tw-bg-neutral-50 hover:tw-scale-[1.02]"
          >
            {closeButtonText}
          </button>
        </div>
      ) : (
        <div className="tw-mt-6 tw-flex tw-flex-col sm:tw-flex-row tw-w-full tw-justify-between tw-items-stretch sm:tw-items-end tw-gap-3">
          <button
            disabled={loading}
            onClick={() => {
              closeModal();
            }}
            className="tw-flex tw-py-3 tw-px-4 tw-justify-center tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-neutral-300 tw-w-full sm:tw-w-auto sm:tw-min-w-[120px] tw-self-stretch tw-text-sm tw-font-medium tw-text-neutral-600 tw-bg-white tw-min-h-[48px] tw-transition-all tw-duration-200 hover:tw-bg-neutral-50 hover:tw-border-neutral-400 hover:tw-shadow-sm hover:tw-scale-[1.02]"
          >
            {closeButtonText}
          </button>
          <CheckoutButton selectedPlan={selectedPlan} loading={loading} setLoading={setLoading} currency={currency} />
        </div>
      )}
      <RiskFreeMessaging selectedPlan={selectedPlan} selectedPlanPrice={selectedPlanPrice} />
    </div>
  );
};

const CheckoutButton = (props: {
  selectedPlan: SubscriptionPlan | 'lifetime' | null;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  currency: {
    code: SupportedCurrencyCode;
    symbol: string;
  };
}) => {
  const { selectedPlan, loading, setLoading, currency } = props;
  
  // Always enable the button, but show different messaging
  const getButtonText = () => {
    if (loading) return 'Processing...';
    if (selectedPlan === null) return 'Choose Plan & Start Free Trial';
    if (selectedPlan === 'premium_monthly') return 'Start Free Trial - Monthly';
    if (selectedPlan === 'premium_yearly') return 'Start Free Trial - Annual';
    if (selectedPlan === 'lifetime') return 'Get Lifetime Access';
    return 'Start Free Trial';
  };
  
  const handleClick = async () => {
    setLoading(true);
    
    // If no plan selected, default to annual (best value)
    const planToUse = selectedPlan || 'premium_yearly';
    
    const currencyCode = currency.code;
    try {
      const response = await createCheckoutSession({
        planId: planToUse,
        isUpgrade: false,
        currency: isSupportedCurrencyCode(currencyCode) ? currencyCode : 'usd'
      });
      const redirectLocation = response?.data?.location;
      if (!redirectLocation) {
        throw new Error('failed to create strip customer portal');
      }
      window.location.assign(redirectLocation);
    } catch (error) {
      setLoading(false);
      window.alert(
        'There was a problem creating stripe checkout session. Please try again. If the issue persists, let us know at support@jumblejournal.org'
      );
      console.log(error);
    }
  };
  
  return (
    <div className="tw-flex tw-self-stretch tw-flex-1 tw-justify-center tw-items-center">
      <button
        disabled={loading}
        className={`tw-w-full tw-flex tw-py-4 tw-px-6 tw-justify-center tw-items-center tw-gap-2 tw-rounded-lg tw-border-2 tw-self-stretch tw-text-base tw-font-semibold tw-transition-all tw-duration-200 tw-min-h-[56px] ${
          selectedPlan 
            ? 'tw-bg-primary-800 tw-border-primary-800 tw-text-white hover:tw-bg-primary-900 hover:tw-shadow-lg hover:tw-scale-[1.02]' 
            : 'tw-bg-primary-600 tw-border-primary-600 tw-text-white hover:tw-bg-primary-700 hover:tw-shadow-lg hover:tw-scale-[1.02] tw-animate-pulse'
        }`}
        type="button"
        onClick={handleClick}
      >
        {loading && (
          <span className="tw-mr-4 tw-text-center tw-h-4 tw-w-4 spinner-border" role="status" aria-hidden="true"></span>
        )}
        {getButtonText()}
      </button>
    </div>
  );
};

const ValuePropositionSection = () => {
  return (
    <div className="tw-w-full tw-flex tw-flex-col tw-gap-3">
      <div className="tw-w-full tw-flex tw-flex-col tw-gap-2 tw-py-4 tw-px-5 tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg">
        <div className="tw-text-center tw-text-sm tw-font-semibold tw-text-blue-900">🎯 Risk-Free Trial • 📞 Same-Day Support • 🔒 Private & Secure</div>
        <div className="tw-text-center tw-text-xs tw-text-blue-700">Cancel anytime • No charges during trial period</div>
      </div>
      
      {/* Gentle encouragement instead of urgency */}
      <div className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-3 tw-px-4 tw-bg-green-50 tw-border tw-border-green-200 tw-rounded-lg">
        <div className="tw-text-sm tw-text-green-800 tw-text-center">
          <span className="tw-font-medium">✨ Start your journaling journey today</span>
          <div className="tw-text-xs tw-mt-1 tw-text-green-700">Take the first step toward better mental clarity</div>
        </div>
      </div>
    </div>
  );
};

const PremiumFeatures = () => {
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  
  // Show only key features initially
  const keyFeatures = premiumFeatures.slice(0, 4);
  const additionalFeatures = premiumFeatures.slice(4);
  
  return (
    <div className="tw-w-full tw-flex tw-flex-col tw-gap-3">
      <div className="tw-text-center tw-text-sm tw-font-medium tw-text-neutral-600">What's included:</div>
      
      {/* Key Features - Always Visible - Mobile Optimized */}
      <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3 tw-w-full">
        {keyFeatures.map((feature, index) => (
          <div key={`key-feature-${index}`} className="tw-flex tw-items-start tw-gap-3 tw-p-3 tw-bg-neutral-50 tw-rounded-lg">
            <div className="tw-text-xl tw-min-w-[28px]">{feature.icon}</div>
            <div className="tw-flex tw-flex-col tw-gap-1">
              <div className="tw-text-sm tw-font-semibold tw-text-neutral-800">{feature.name}</div>
              <div className="tw-text-xs tw-text-neutral-600 tw-leading-relaxed">{feature.description}</div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Expandable Additional Features - Mobile Optimized */}
      {showAllFeatures && (
        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3 tw-w-full tw-border-t tw-border-neutral-200 tw-pt-4">
          {additionalFeatures.map((feature, index) => (
            <div key={`additional-feature-${index}`} className="tw-flex tw-items-start tw-gap-3 tw-p-3 tw-bg-neutral-50 tw-rounded-lg">
              <div className="tw-text-xl tw-min-w-[28px]">{feature.icon}</div>
              <div className="tw-flex tw-flex-col tw-gap-1">
                <div className="tw-text-sm tw-font-semibold tw-text-neutral-800">{feature.name}</div>
                <div className="tw-text-xs tw-text-neutral-600 tw-leading-relaxed">{feature.description}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Toggle Button */}
      <button
        onClick={() => setShowAllFeatures(!showAllFeatures)}
        className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-2 tw-text-sm tw-font-medium tw-text-primary-600 tw-hover:text-primary-700 tw-transition-colors"
      >
        {showAllFeatures ? (
          <>
            <span>See fewer features</span>
            <ChevronUp className="tw-w-4 tw-h-4" />
          </>
        ) : (
          <>
            <span>See all features ({additionalFeatures.length} more)</span>
            <ChevronDown className="tw-w-4 tw-h-4" />
          </>
        )}
      </button>
    </div>
  );
};

const RiskFreeMessaging = (props: {
  selectedPlan: SubscriptionPlan | 'lifetime' | null;
  selectedPlanPrice: string | undefined;
}) => {
  const { selectedPlan, selectedPlanPrice } = props;
  
  if (selectedPlan === 'lifetime') {
    return (
      <div className="tw-flex tw-flex-col tw-justify-center tw-items-start tw-self-start tw-gap-0.5">
        <div className="tw-text-xs tw-text-neutral-600">{`${
          selectedPlanPrice && selectedPlanPrice.replace('/', 'per')
        }`}</div>
        <div className="tw-text-base tw-font-bold tw-text-neutral-800">💰 30-Day Money-Back Guarantee</div>
        <div className="tw-text-xs tw-text-neutral-600">No questions asked • Full refund</div>
      </div>
    );
  }
  
  if (selectedPlanPrice) {
    return (
      <div className="tw-flex tw-flex-col tw-justify-center tw-items-start tw-self-start tw-gap-0.5">
        <div className="tw-text-xs tw-text-neutral-600">🚀 Try Jumble Premium Risk-Free</div>
        <div className="tw-text-base tw-font-bold tw-text-neutral-800">{`${config.freeTrialPeriodInDays} days free trial`}</div>
        <div className="tw-text-xs tw-text-neutral-600">{`Then ${
          selectedPlanPrice && selectedPlanPrice.replace('/', 'per')
        } • Cancel anytime`}</div>
      </div>
    );
  }
  
  return null;
};

const EncouragementMessage = (props: { nickname: string; isLandingPage: boolean }) => {
  const { nickname } = props;
  const messages = [
    `Hello ${nickname || '👋'},`,
    'Thank you for considering Jumble.',
    'This membership is an investment in yourself. Journaling improves clarity, communication, and overall mental health.',
    ' Our team works every day to make Jumble the best journaling experience in the world. We hope you enjoy.',
    'Your support means the world to us.'
  ];
  return (
    <div className="tw-text-base tw-text-neutral-600 tw-flex tw-flex-col tw-items-start tw-gap-4">
      {messages.map((msg, index) => {
        return (
          <div key={`encouragement-${index}`} className="tw-text-left">
            {msg}
          </div>
        );
      })}
      <div>
        <div className="tw-font-bold tw-text-left">Tony Oreglia | Founder of Jumble</div>
      </div>
    </div>
  );
};

export default ChoosePlan;
